/**
 * OAuth handoff screen — GETs /auth/oauth/handoff/:id, stores the token,
 * navigates to return_to.
 *
 * CONTRACT (unchanged — OAuthHandoffScreen.test.tsx covers it):
 *   - Reads `handoff_id` from `/auth/handoff/:id` and GETs the server's
 *     one-shot consume endpoint (`apps/api/garden/auth/oauth/handoff.py`).
 *   - On success: store the access_token, navigate to `return_to`
 *     (default `/chat`; sanitised by `safeReturnToOrNull`, the one sanitiser — W0.1).
 *   - On failure — including the provider's own `?error=access_denied`
 *     redirect — render a localized message with a way back.
 *   - The `fired` ref keeps StrictMode's double mount from consuming the
 *     one-shot handoff twice; the second GET would 404.
 *
 * ── The counter rebuild (2026-08) ────────────────────────────────────────────
 *
 * This screen is a few hundred milliseconds of someone else's redirect chain,
 * and it used to be a bare spinner: an unlabelled circle telling the user
 * nothing about what was happening or who was doing it. It now says what work
 * is in progress, with the `.g-shimmer` sweep the design system reserves for
 * NAMED work — the label stays readable the whole time and the motion carries
 * the liveness, so the user learns what is happening rather than only that
 * something is.
 *
 * The failure state is a slip notice with the specific provider error and a
 * link back to the door, not a red sentence floating on a blank page. The link
 * copy now says "back to sign-in" because that is where it goes — it used to
 * read "to the home page" while routing to /login.
 */
import { OctagonAlert } from "lucide-react";
import { useEffect, useRef, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { AuthCounter } from "@/features/auth/components/AuthCounter";
import { AuthLink } from "@/features/auth/components/controls";
import { SlipNotice } from "@/features/auth/components/slip";
import { safeReturnToOrNull, withReturnTo } from "@/features/auth/destination";
import { ApiError, NetworkError, apiClient, AUTH_PATHS } from "@/lib/api-client";
import { useAuthStore } from "@/lib/auth-store";
import { iconProps } from "@/lib/icons";

interface HandoffResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  user: {
    id: string;
    email: string;
    language: "ru" | "ky" | "en";
  };
}

function mapHandoffError(err: unknown): string {
  if (err instanceof NetworkError) return "errors.network";
  if (!(err instanceof ApiError)) return "errors.generic";
  switch (err.code) {
    case "auth.oauth.state_mismatch":
      return "errors.auth.oauth.state_mismatch";
    case "auth.oauth.state_expired":
      return "errors.auth.oauth.state_expired";
    case "auth.oauth.id_token_invalid":
      return "errors.auth.oauth.id_token_invalid";
    case "auth.oauth.email_unverified_at_provider":
      return "errors.auth.oauth.email_unverified_at_provider";
    case "auth.oauth.linking_rejected_unverified":
      return "errors.auth.oauth.linking_rejected_unverified";
    default:
      return "oauth.handoffError";
  }
}

export function OAuthHandoffScreen(): ReactElement {
  const { t } = useTranslation("auth");
  const { id: handoffId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setAccessToken = useAuthStore((s) => s.setAccessToken);

  const [errorKey, setErrorKey] = useState<string | null>(null);

  // React StrictMode mounts components twice in dev — without this ref the
  // handoff GET would fire twice and the second call would 404 (the server's
  // MULTI/EXEC delete removed the entry).
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current) return;
    fired.current = true;

    // OAuth provider error redirect: `/auth/handoff/:id?error=access_denied`.
    const providerError = searchParams.get("error");
    if (providerError !== null && providerError !== "") {
      const key =
        providerError === "access_denied"
          ? "oauth.errorCanceled"
          : "oauth.errorProvider";
      setErrorKey(key);
      return;
    }

    if (handoffId === undefined || handoffId === "") {
      setErrorKey("oauth.handoffError");
      return;
    }

    const returnTo = safeReturnToOrNull(searchParams.get("return_to")) ?? "/chat";
    const path = AUTH_PATHS.oauthHandoff + "/" + encodeURIComponent(handoffId);

    void (async () => {
      try {
        const data = await apiClient.get<HandoffResponse>(path, {
          // Handoff is GET-only; the refresh interceptor would never fire,
          // but explicit-is-better-than-implicit.
          skipRefreshOn401: true,
        });
        setAccessToken(data.access_token);
        navigate(returnTo, { replace: true });
      } catch (err) {
        setErrorKey(mapHandoffError(err));
      }
    })();
  }, [handoffId, searchParams, setAccessToken, navigate]);

  if (errorKey !== null) {
    return (
      <AuthCounter
        door="consumer"
        doorName={t("oauth.doorName")}
        title={t("oauth.handoffTitle")}
        testId="oauth-handoff-error"
      >
        <div
          className="gav2-stack-md flex flex-col"
          role="alert"
          aria-label={t("oauth.handoffA11yLabel")}
        >
          <SlipNotice
            tone="error"
            icon={<OctagonAlert {...iconProps("sm")} />}
          >
            <span data-testid="oauth-handoff-error-message">{t(errorKey)}</span>
          </SlipNotice>
          <AuthLink
            to={withReturnTo("/login", searchParams.get("return_to"))}
            testId="oauth-handoff-retry-link"
          >
            {t("oauth.handoffRetry")}
          </AuthLink>
        </div>
      </AuthCounter>
    );
  }

  return (
    <AuthCounter
      door="consumer"
      doorName={t("oauth.doorName")}
      title={t("oauth.handoffTitle")}
      testId="oauth-handoff-screen"
    >
      <div
        role="status"
        aria-live="polite"
        aria-label={t("oauth.handoffA11yLabel")}
      >
        {/* Named work: the sentence says what is being done, the sweep says it
            is still happening. `.g-shimmer` pins to readable ink under
            prefers-reduced-motion — the label never becomes invisible. */}
        <p className="g-shimmer text-sm font-medium">{t("oauth.handoffLede")}</p>
      </div>
    </AuthCounter>
  );
}

export default OAuthHandoffScreen;
